import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, API } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Lock, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const LoginPage = () => {
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { login } = useAuth();

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/auth/login`, { password });
      
      if (response.data.success) {
        login(response.data.token);
        toast.success("Welcome back!");
        navigate("/");
      } else {
        toast.error(response.data.message || "Invalid password");
      }
    } catch (error) {
      toast.error("Login failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      {/* Left side - Background Image */}
      <div 
        className="hidden lg:flex lg:w-1/2 relative bg-cover bg-center"
        style={{
          backgroundImage: `linear-gradient(to right, rgba(0, 52, 29, 0.85), rgba(0, 52, 29, 0.7)), url('https://images.unsplash.com/photo-1675726061078-01a91334f26f?crop=entropy&cs=srgb&fm=jpg&q=85')`
        }}
      >
        <div className="absolute inset-0 flex flex-col justify-center px-16">
          <img 
            src="https://customer-assets.emergentagent.com/job_665c49ba-1471-4262-8719-d672e8150062/artifacts/l1uvt1c1_Logo_Framed_Green.png" 
            alt="Land ClearCo Logo" 
            className="w-48 mb-8"
          />
          <h1 className="font-heading text-5xl font-bold text-white uppercase tracking-tight mb-4">
            Quote Management
          </h1>
          <p className="text-white/80 text-lg max-w-md">
            Create, manage, and send professional quotes for land clearing services.
          </p>
        </div>
      </div>

      {/* Right side - Login Form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-zinc-50">
        <div className="w-full max-w-md">
          {/* Mobile Logo */}
          <div className="lg:hidden text-center mb-8">
            <img 
              src="https://customer-assets.emergentagent.com/job_665c49ba-1471-4262-8719-d672e8150062/artifacts/l1uvt1c1_Logo_Framed_Green.png" 
              alt="Land ClearCo Logo" 
              className="w-32 mx-auto mb-4"
            />
          </div>

          <Card className="shadow-lg border-0 bg-white">
            <CardHeader className="text-center pb-2">
              <div className="mx-auto w-14 h-14 bg-[#00341d] rounded-full flex items-center justify-center mb-4">
                <Lock className="w-6 h-6 text-white" />
              </div>
              <CardTitle className="font-heading text-2xl uppercase tracking-tight text-[#00341d]">
                Admin Access
              </CardTitle>
              <CardDescription className="text-zinc-500">
                Enter your password to continue
              </CardDescription>
            </CardHeader>
            <CardContent className="pt-4">
              <form onSubmit={handleLogin} className="space-y-6">
                <div className="relative">
                  <Input
                    type={showPassword ? "text" : "password"}
                    placeholder="Enter password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="h-12 pr-12 bg-zinc-50/50 border-zinc-200 focus:bg-white"
                    data-testid="password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-600"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
                <Button
                  type="submit"
                  disabled={loading || !password}
                  className="w-full h-12 bg-[#00341d] hover:bg-[#002816] text-white font-bold uppercase tracking-wide rounded-sm transition-transform duration-200 hover:-translate-y-0.5 active:translate-y-0"
                  data-testid="login-button"
                >
                  {loading ? (
                    <span className="flex items-center gap-2">
                      <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Signing in...
                    </span>
                  ) : (
                    "Sign In"
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>

          <p className="text-center text-sm text-zinc-400 mt-8">
            Land ClearCo, LLC &copy; {new Date().getFullYear()}
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
